import { useEffect, useState } from "react";
import {
  Home,
  Package,
  Truck,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from "lucide-react";

import Logo from "./Logo";
import NavItem from "./NavItem";

const SIDEBAR_KEY = "fpccs-sidebar-collapsed";

export default function Sidebar({
  currentPath,
  onNavigate,
  onLogout,
  user,
  collapsible = true,
}) {
  const [collapsed, setCollapsed] = useState(() => {
    if (!collapsible) return false;
    return localStorage.getItem(SIDEBAR_KEY) === "true";
  });

  useEffect(() => {
    if (collapsible) {
      localStorage.setItem(SIDEBAR_KEY, String(collapsed));
    }
  }, [collapsed, collapsible]);

  const menus = [
    { label: "Beranda", path: "/", icon: <Home size={20} /> },
    { label: "Master Material", path: "/materials", icon: <Package size={20} /> },
    { label: "Master Supplier", path: "/suppliers", icon: <Truck size={20} /> },
    { label: "Pengaturan", path: "/settings", icon: <Settings size={20} /> },
  ];

  const settingPaths = ['/settings', '/users', '/audit-logs', '/system', '/updater', '/branding', '/license', '/monitoring', '/account'];
  const isActive = (path) => path === '/'
    ? currentPath === '/' || currentPath.startsWith('/projects/')
    : path === '/settings'
      ? settingPaths.some((item) => currentPath === item || currentPath.startsWith(`${item}/`))
      : currentPath === path || currentPath.startsWith(`${path}/`);

  return (
    <aside
      className={`flex h-full min-h-screen flex-col border-r border-[var(--fpccs-border)] bg-[var(--fpccs-sidebar)] shadow-[var(--fpccs-shadow-soft)] backdrop-blur-xl transition-all duration-300 lg:h-screen ${
        collapsed ? "w-[72px]" : "w-[240px]"
      }`}
    >
      <div
        className={`flex h-[84px] items-center ${
          collapsed ? "justify-center px-3" : "justify-between px-4"
        }`}
      >
        <Logo collapsed={collapsed} />

        {collapsible && (
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? "Buka sidebar" : "Tutup sidebar"}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[var(--fpccs-text-muted)] transition hover:bg-[var(--fpccs-surface-2)] hover:text-[var(--fpccs-text)]"
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        )}
      </div>

      <div className="border-b border-[var(--fpccs-border)]" />

      <nav className="flex-1 space-y-2 p-3">
        {menus.map((menu) => (
          <NavItem
            key={menu.path}
            icon={menu.icon}
            label={menu.label}
            collapsed={collapsed}
            active={isActive(menu.path)}
            onClick={() => onNavigate(menu.path)}
          />
        ))}
      </nav>

      <div className="border-t border-[var(--fpccs-border)] p-3">
        <div className={`mb-2 rounded-xl bg-[var(--fpccs-surface-2)] ${collapsed ? 'p-2' : 'px-3 py-2.5'}`}>
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2.5'}`}>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgba(var(--fpccs-primary-rgb),0.14)] text-xs font-semibold text-[var(--fpccs-primary)]">{(user?.name || user?.email || '?').slice(0, 1).toUpperCase()}</div>
            {!collapsed && <div className="min-w-0"><div className="truncate text-xs font-semibold">{user?.name || 'Pengguna'}</div><div className="truncate text-[10px] text-subtle-fpccs">{user?.email || '-'}</div><div className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-[var(--fpccs-primary)]">{user?.role || '-'}</div></div>}
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          aria-label="Keluar"
          className={`flex w-full items-center rounded-xl py-3 text-[var(--fpccs-text-muted)] transition hover:bg-red-500/10 hover:text-red-400 ${
            collapsed ? "justify-center px-2" : "gap-3 px-3"
          }`}
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm font-medium">Keluar</span>}
        </button>
      </div>
    </aside>
  );
}
